import { LanguageMessage, parseLanguageMessage, serializeLanguageMessage } from "./languageMessage";
import { RequestField, fromInputParam, toInputParam } from "./requestField";
import { ResponseField, fromReturnParam, toReturnParam } from "./responseField";
import { encodeContextFilter, decodeContextFilter } from "./contextFilter";
import { TriggerEvent, decideCopy, showsSequencing, showsCallStatusValues } from "./sections";

export type MessageKey = "waiting" | "success" | "failure";
export const MESSAGE_KEYS: MessageKey[] = ["waiting", "success", "failure"];

export const MESSAGE_FIELDS: Record<MessageKey, "backchannelMessage" | "successResponse" | "failureResponse"> = {
  waiting: "backchannelMessage",
  success: "successResponse",
  failure: "failureResponse",
};

export const MESSAGE_LABELS: Record<MessageKey, string> = {
  waiting: "Say while waiting",
  success: "Say on success",
  failure: "Say if it fails",
};

/** Per-language limits, carried over from the old single-text limits. */
export const MESSAGE_LIMITS: Record<MessageKey, number> = { waiting: 600, success: 1000, failure: 1000 };
export const NAME_LIMIT = 50;
export const PROMPT_LIMIT = 25000;

export type HttpMethod = "GET" | "POST" | "PUT";

export type EditorState = {
  apiName: string;
  triggerEvent: TriggerEvent;
  callDirection: string[];
  callOnce: boolean;
  /** Comma-separated text while editing; an array in the document. */
  callStatusMatchValues: string;
  apiGatePrompt: string;
  apiTriggerPrompt: string;
  apiMethod: HttpMethod;
  apiURL: string;
  messages: Record<MessageKey, LanguageMessage>;
  requestFields: RequestField[];
  responseFields: ResponseField[];
  /** Encoded "characteristic::value" strings (see contextFilter.js). */
  contextFilter: string[];
  sequenceAPIIds: string[];
  activationAPIIds: string[];
};

const KNOWN_EVENTS: TriggerEvent[] = ["callStart", "duringCall", "callEnd", "callStatusWebhook"];

export function editorStateFromItem(item: any): EditorState {
  const source = item ?? {};
  const status = source.callStatusMatchValues;
  return {
    apiName: source.apiName ?? "",
    triggerEvent: KNOWN_EVENTS.includes(source.triggerEvent) ? source.triggerEvent : "duringCall",
    callDirection: Array.isArray(source.callDirection) ? [...source.callDirection] : ["inbound", "outbound"],
    callOnce: typeof source.callOnce === "boolean" ? source.callOnce : true,
    callStatusMatchValues: Array.isArray(status) ? status.join(", ") : typeof status === "string" ? status : "",
    apiGatePrompt: source.apiGatePrompt ?? "",
    apiTriggerPrompt: source.apiTriggerPrompt ?? "",
    apiMethod: source.apiMethod ?? "POST",
    apiURL: source.apiURL ?? "",
    messages: {
      waiting: parseLanguageMessage(source.backchannelMessage ?? ""),
      success: parseLanguageMessage(source.successResponse ?? ""),
      failure: parseLanguageMessage(source.failureResponse ?? ""),
    },
    requestFields: (Array.isArray(source.inputParams) ? source.inputParams : []).map(fromInputParam),
    responseFields: (Array.isArray(source.returnParams) ? source.returnParams : []).map(fromReturnParam),
    contextFilter: encodeContextFilter(Array.isArray(source.contextFilter) ? source.contextFilter : []),
    sequenceAPIIds: Array.isArray(source.sequenceAPIIds) ? [...source.sequenceAPIIds] : [],
    activationAPIIds: Array.isArray(source.activationAPIIds) ? [...source.activationAPIIds] : [],
  };
}

function splitStatusValues(text: string): string[] {
  return (text ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

export function payloadFromEditorState(state: EditorState, defaultLanguage: string) {
  const event = state.triggerEvent;
  const quickCheckApplies = decideCopy(event)?.showsQuickCheck === true;
  const sequencingApplies = showsSequencing(event);
  return {
    apiName: (state.apiName ?? "").trim(),
    apiURL: (state.apiURL ?? "").trim(),
    apiMethod: state.apiMethod,
    triggerEvent: event,
    apiTriggerPrompt: state.apiTriggerPrompt ?? "",
    apiGatePrompt: quickCheckApplies ? state.apiGatePrompt ?? "" : "",
    callOnce: state.callOnce,
    callStatusMatchValues: showsCallStatusValues(event) ? splitStatusValues(state.callStatusMatchValues) : [],
    callDirection: [...state.callDirection],
    backchannelMessage: serializeLanguageMessage(state.messages.waiting, defaultLanguage),
    successResponse: serializeLanguageMessage(state.messages.success, defaultLanguage),
    failureResponse: serializeLanguageMessage(state.messages.failure, defaultLanguage),
    inputParams: state.requestFields.map(toInputParam),
    returnParams: state.responseFields.map(toReturnParam),
    contextFilter: decodeContextFilter(state.contextFilter),
    sequenceAPIIds: sequencingApplies ? [...state.sequenceAPIIds] : [],
    activationAPIIds: sequencingApplies ? [...state.activationAPIIds] : [],
  };
}

function longestVariant(message: LanguageMessage): number {
  if (message.mode === "same") return (message.text ?? "").trim().length;
  return Object.keys(message.variants).reduce((max, code) => Math.max(max, (message.variants[code] ?? "").trim().length), 0);
}

/** Errors keyed by field ("apiName", "messages.waiting", …); empty when the API can be saved. */
export function validateEditorState(state: EditorState): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = (state.apiName ?? "").trim();
  if (name.length === 0) errors.apiName = "Give this API a name.";
  else if (name.length > NAME_LIMIT) errors.apiName = `Keep the name under ${NAME_LIMIT} characters.`;

  if ((state.apiURL ?? "").trim().length === 0) errors.apiURL = "Enter the URL to send the request to.";
  if (!Array.isArray(state.callDirection) || state.callDirection.length === 0) errors.callDirection = "Choose at least one call direction.";

  if ((state.apiGatePrompt ?? "").length > PROMPT_LIMIT) errors.apiGatePrompt = `Keep this under ${PROMPT_LIMIT.toLocaleString()} characters.`;
  if ((state.apiTriggerPrompt ?? "").length > PROMPT_LIMIT) errors.apiTriggerPrompt = `Keep this under ${PROMPT_LIMIT.toLocaleString()} characters.`;

  for (const key of MESSAGE_KEYS) {
    if (longestVariant(state.messages[key]) > MESSAGE_LIMITS[key]) {
      errors[`messages.${key}`] = `Keep each language under ${MESSAGE_LIMITS[key]} characters.`;
    }
  }
  return errors;
}
